import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import classNames from "classnames";
import Button from "components/Button";
import Dialog from "components/Dialog";
import LoginOrSignupForm from "components/LoginOrSignupForm";
import Spacer from "components/Spacer";
import StripePaymentForm, {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import {
  setSelectedBillingInterval,
  setSelectedPlanPackage,
} from "features/slices/buyFlowSlice";
import {
  setSelectedCmsPlan,
  setSignupCharges,
} from "features/slices/shopPagePhaseTwoSlice";
import {
  BillingInterval,
  SeatDetails,
  SectionDetails,
  TicketCartDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useAuth from "hooks/useAuth";
import useCurrentUser from "hooks/useCurrentUser";
import { CmsPlan, ShopPagePhaseTwo } from "lib/sanity.types";
import isEmpty from "lodash/isEmpty";
import {
  fetchSignupChargeRequest,
  updateBillingFetcher,
} from "queries/account.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import {
  fetchPlanPackageAvailableSections,
  holdSeasonTicketSeatsRequest,
  useFetchPlanPackageAvailableSectionsQuery,
  useFetchPlanPackageSectionAvailableBlocksQuery,
  useFetchPlanPackagesQuery,
} from "queries/planPackage.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import {
  addParkingPackageSeatsRequest,
  calculateSeasonTicketSeatChargeRequest,
  confirmSeasonTicketPurchaseRequest,
  deleteTicketCartFetcher,
} from "queries/ticket.queries";
import { useFetchSeatsBySectionQuery } from "queries/venue.queries";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, ScrollView, View, useWindowDimensions } from "react-native";
import VerifyPhonePopup from "screens/Dashboard/components/VerifyPhonePopup";
import SeatQuantitySelector from "screens/SeatSelection/components/SeatQuantitySelector";
import VenueSeatMap from "screens/SeatSelection/components/VenueSeatMap";
import { formatCurrency } from "utils/appUtils";
import { toDashboard } from "utils/navigationUtils";
import toast from "utils/toast";
import AvailableSeatDots from "./components/AvailableSeatDots";
import PlanContainer from "./components/PlanContainer";
import SeasonPlanOrderSummary from "./components/SeasonPlanOrderSummary";
import { planIdProperty } from "./helpers";

type TdcSeatSelection = { section: string; row: string; seat: string };

const InlineErrorBanner = ({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) => {
  return (
    <View className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
      <View className="flex-row items-center justify-between gap-3">
        <Text category="c1">{message}</Text>
        {actionLabel && onAction && (
          <Button appearance="ghost" size="tiny" onPress={onAction}>
            {actionLabel}
          </Button>
        )}
      </View>
    </View>
  );
};

const SeasonTicketCheckoutFlow = ({
  shopPage,
}: {
  shopPage: ShopPagePhaseTwo;
}) => {
  const dispatch = useAppDispatch();
  const { group, selectedCmsPlan } = useAppSelector(
    (state) => state.shopPagePhaseTwo,
  );
  const { tenantThemeColor } = useAppSelector((state) => state.tenant);
  const { selectedBillingInterval, selectedPlanPackage } = useAppSelector(
    (state) => state.buyFlow,
  );
  const { isLoggedIn } = useAuth();
  const { account, fetchAccount } = useAccount();
  const { currentUser } = useCurrentUser();

  const { data: branding } = useFetchTenantBrandingQuery();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const tenantSubdomain = branding?.subdomain;

  const eventType = shopPage.seasonPassProductEventType;
  const venueName = shopPage.seasonPassProductVenueName;
  const canRenderSeatMap =
    !!branding?.type && !!tenantSubdomain && !!eventType && !!venueName;
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;

  const selectedPlanPackageId = selectedCmsPlan?.[planIdProperty] as
    | number
    | undefined;

  const tdcPackageListId = selectedCmsPlan?.tdcPackageListId;
  const tdcParkingPackageListId = selectedCmsPlan?.tdcParkingPackageListId;
  const tdcParkingPackageBuyerTypeId =
    selectedCmsPlan?.tdcParkingPackageBuyerTypeId;
  const tdcPackageId = selectedCmsPlan?.tdcPackageId;
  const tdcPackageBuyerTypeId = selectedCmsPlan?.tdcPackageBuyerTypeId;
  const isTdcSeasonTicketPlan = Boolean(tdcPackageListId && tdcPackageId);

  const {
    data: seatMapSectionsRaw,
    isFetching: isFetchingSections,
    isError: isSeatMapSectionsError,
    refetch: refetchSeatMapSections,
  } = useFetchPlanPackageAvailableSectionsQuery(
    selectedPlanPackageId,
    tdcPackageListId,
    tdcPackageId,
  );

  const seatMapSections = useMemo(
    () => seatMapSectionsRaw || [],
    [seatMapSectionsRaw],
  );
  const [selectedSection, setSelectedSection] = useState<string | undefined>();
  const [venueMapKey, setVenueMapKey] = useState(0);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [verifyPhonePopupVisible, setVerifyPhonePopupVisible] = useState(false);
  const [pendingHoldSeats, setPendingHoldSeats] = useState<
    SeatDetails[] | null
  >(null);
  const [pendingConfirmPurchase, setPendingConfirmPurchase] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState<SeatDetails[]>([]);
  const [cartSeats, setCartSeats] = useState<SeatDetails[]>([]);
  const [holdExpiresAt, setHoldExpiresAt] = useState<Date | null>(null);
  const [holdRemainingMs, setHoldRemainingMs] = useState<number | null>(null);
  const [tdcCartId, setTdcCartId] = useState<string | null>(null);
  const [previewSeatCharge, setPreviewSeatCharge] =
    useState<TicketCartDetails | null>(null);
  const [seatCharge, setSeatCharge] = useState<TicketCartDetails | null>(null);
  const [parkingSections, setParkingSections] = useState<SectionDetails[]>([]);
  const [selectedParkingSection, setSelectedParkingSection] = useState<
    string | undefined
  >(undefined);
  const [parkingDialogVisible, setParkingDialogVisible] = useState(false);
  const [isFetchingParkingSections, setIsFetchingParkingSections] =
    useState(false);
  const [selectedParkingQuantity, setSelectedParkingQuantity] = useState(1);
  const [confirmedParkingSelections, setConfirmedParkingSelections] = useState<
    TdcSeatSelection[]
  >([]);
  const [confirmedParkingSection, setConfirmedParkingSection] = useState<
    string | undefined
  >(undefined);
  const [confirmedParkingSectionLabel, setConfirmedParkingSectionLabel] =
    useState<string | undefined>(undefined);
  const [holdSeatsError, setHoldSeatsError] = useState<string | null>(null);
  const [seatChargeError, setSeatChargeError] = useState<string | null>(null);
  const [parkingSectionsError, setParkingSectionsError] = useState<
    string | null
  >(null);
  const [parkingSubmitError, setParkingSubmitError] = useState<string | null>(
    null,
  );
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const normalizedSelectedSection = useMemo(() => {
    if (!selectedSection) return undefined;
    return selectedSection.split(",")[0]?.trim();
  }, [selectedSection]);

  const {
    data: seatMapBestBlocksRaw,
    isFetching: isFetchingBlocks,
    isError: isSeatBlocksError,
    refetch: refetchSeatBlocks,
  } = useFetchPlanPackageSectionAvailableBlocksQuery(
    selectedPlanPackageId,
    normalizedSelectedSection,
    eventType,
    tdcPackageListId,
    tdcPackageId,
  );

  const seatMapBestBlocks = useMemo(
    () => seatMapBestBlocksRaw || [],
    [seatMapBestBlocksRaw],
  );

  const {
    data: parkingBlocksRaw,
    isFetching: isFetchingParkingBlocks,
    isError: isParkingBlocksError,
    refetch: refetchParkingBlocks,
  } = useFetchPlanPackageSectionAvailableBlocksQuery(
    selectedPlanPackageId,
    selectedParkingSection,
    "PARKING",
    tdcParkingPackageListId,
    tdcPackageId,
  );

  const parkingBlocks = useMemo(
    () => parkingBlocksRaw || [],
    [parkingBlocksRaw],
  );

  const availableParkingTicketCount = useMemo(() => {
    if (!parkingBlocks.length) return 0;
    return parkingBlocks.reduce((total, block) => {
      return total + (block.tickets?.length || 0);
    }, 0);
  }, [parkingBlocks]);

  const maxParkingQuantity = useMemo(
    () => Math.min(availableParkingTicketCount, 20),
    [availableParkingTicketCount],
  );

  const parkingTicketIds = useMemo(() => {
    const ids: number[] = [];
    for (const block of parkingBlocks) {
      for (const ticket of block.tickets) {
        if (ticket.seatId) ids.push(ticket.seatId);
      }
    }
    return ids;
  }, [parkingBlocks]);

  const buildParkingSelections = (quantity: number): TdcSeatSelection[] => {
    const selections: TdcSeatSelection[] = [];
    for (const block of parkingBlocks) {
      for (const ticket of block.tickets) {
        if (ticket.section && ticket.row && ticket.seat) {
          selections.push({
            section: ticket.section,
            row: ticket.row,
            seat: ticket.seat,
          });
        }
        if (selections.length >= quantity) return selections;
      }
    }
    return selections;
  };

  const buildTdcPackageSelections = (
    seatSelections: TdcSeatSelection[],
    parkingSelections: TdcSeatSelection[],
  ) => {
    const selections: Array<{
      tdcPackageListId: string;
      tdcPackageId: string;
      selections: TdcSeatSelection[];
    }> = [];

    if (
      tdcPackageListId &&
      tdcPackageId &&
      seatSelections &&
      seatSelections.length > 0
    ) {
      selections.push({
        tdcPackageListId,
        tdcPackageId,
        selections: seatSelections,
      });
    }

    if (
      tdcParkingPackageListId &&
      tdcPackageId &&
      parkingSelections &&
      parkingSelections.length > 0
    ) {
      selections.push({
        tdcPackageListId: tdcParkingPackageListId,
        tdcPackageId,
        selections: parkingSelections,
      });
    }

    return selections;
  };

  const availableSeatIds = useMemo(() => {
    const ids = new Set<number>();
    for (const block of seatMapBestBlocks) {
      for (const ticket of block.tickets) {
        if (ticket.seatId) ids.add(ticket.seatId);
      }
    }
    return ids;
  }, [seatMapBestBlocks]);

  const { data: planPackages } = useFetchPlanPackagesQuery();
  const { mutateAsync: fetchSignupCharge } = useMutation(
    fetchSignupChargeRequest,
  );
  const { mutateAsync: updateBilling, isLoading: isUpdatingBilling } =
    useMutation(updateBillingFetcher);

  const {
    mutateAsync: calculateSeatChargePreview,
    isLoading: isPreviewingSeatCharge,
  } = useMutation(calculateSeasonTicketSeatChargeRequest);
  const {
    mutateAsync: calculateSeatChargeFinal,
    isLoading: isCalculatingSeatCharge,
  } = useMutation(calculateSeasonTicketSeatChargeRequest);
  const {
    mutateAsync: confirmSeasonTicketPurchase,
    isLoading: isConfirmingSeasonTicketPurchase,
  } = useMutation(confirmSeasonTicketPurchaseRequest);
  const { mutateAsync: deleteTicketCart, isLoading: isDeletingTicketCart } =
    useMutation(deleteTicketCartFetcher);
  const {
    mutateAsync: addParkingPackageSeats,
    isLoading: isAddingParkingPackageSeats,
  } = useMutation(addParkingPackageSeatsRequest);

  const fetchParkingSections = async (resetSelection: boolean) => {
    if (!selectedPlanPackageId || !tdcParkingPackageListId || !tdcPackageId)
      return;

    setIsFetchingParkingSections(true);
    setParkingDialogVisible(true);
    setParkingSections([]);
    setParkingSectionsError(null);
    setParkingSubmitError(null);
    if (resetSelection) {
      setSelectedParkingSection(undefined);
      setSelectedParkingQuantity(1);
    }

    try {
      const sections = await fetchPlanPackageAvailableSections(
        selectedPlanPackageId,
        {
          tdcPackageListId: tdcParkingPackageListId,
          packageId: tdcPackageId,
        },
      );
      setParkingSections(sections || []);
    } catch {
      setParkingSections([]);
      setParkingSectionsError("Couldn't load parking sections. Try again.");
    } finally {
      setIsFetchingParkingSections(false);
    }
  };

  const handleSelectParkingSection = (sectionValue: string) => {
    setSelectedParkingSection(sectionValue);
    setSelectedParkingQuantity(1);
    setParkingSubmitError(null);
  };

  const Cmsplans = useMemo(
    () => shopPage.plans.filter((plan) => plan.group === group),
    [group, shopPage.plans],
  );
  const CmsplanIds = useMemo(
    () => Cmsplans.map((plan) => plan[planIdProperty]),
    [Cmsplans],
  );
  const actualPlanPackages = useMemo(() => {
    if (!planPackages) return undefined;
    return planPackages.filter((planPackage) =>
      CmsplanIds.includes(planPackage.id),
    );
  }, [planPackages, CmsplanIds]);

  const billingIntervals = [
    { label: "Payment Plan", value: "MONTHLY" },
    { label: "Pay In Full", value: "FULL_SEASON" },
  ];

  const shouldShowBillingInterval = useMemo(() => {
    if (isEmpty(actualPlanPackages)) return false;
    return actualPlanPackages![0].billingIntervals.length > 1;
  }, [actualPlanPackages]);

  const setActualPlanPackageInBuyFlowSlice = (plan: CmsPlan) => {
    const actualPlanPackage = actualPlanPackages?.find(
      (p) => p.id === plan[planIdProperty],
    );
    if (actualPlanPackage !== undefined) {
      dispatch(setSelectedPlanPackage(actualPlanPackage));
    }
  };

  const fetchSignupCharges = async () => {
    if (!actualPlanPackages || actualPlanPackages.length === 0) return;
    const result = [];
    for (const planPackage of actualPlanPackages) {
      for (const billingInterval of planPackage.billingIntervals) {
        const signupCharge = await fetchSignupCharge({
          planPackageIds: [planPackage.id],
          billingInterval,
        });
        result.push({
          planPackageId: planPackage.id,
          billingInterval,
          signupCharge,
        });
      }
    }
    dispatch(setSignupCharges(result));
  };

  useEffect(() => {
    if (!group) return;
    if (Cmsplans.length === 0) return;
    dispatch(setSelectedCmsPlan(Cmsplans[0]));
  }, [Cmsplans, dispatch, group]);

  useEffect(() => {
    if (!group) return;
    if (isEmpty(actualPlanPackages)) return;
    dispatch(
      setSelectedBillingInterval(actualPlanPackages![0].billingIntervals[0]),
    );
    fetchSignupCharges();
    if (Cmsplans.length > 0) setActualPlanPackageInBuyFlowSlice(Cmsplans[0]);
  }, [actualPlanPackages, group, planPackages]);

  useEffect(() => {
    if (!loginModalVisible) return;
    if (!isLoggedIn) return;
    setLoginModalVisible(false);
  }, [isLoggedIn, loginModalVisible]);

  const { mutateAsync: holdSeasonTickets, isLoading: isHoldingSeasonTickets } =
    useMutation(holdSeasonTicketSeatsRequest);

  const holdSelectedSeats = async (seats: SeatDetails[]) => {
    if (!selectedPlanPackageId) {
      toast.error("Please select a plan first.");
      return;
    }
    if (!seats.length) return;

    const section = seats[0].section;
    const seatIds = seats.map((s) => s.id).filter((id): id is number => !!id);
    if (!seatIds.length) return;

    if (chargePreviewDebounceRef.current) {
      clearTimeout(chargePreviewDebounceRef.current);
    }
    chargePreviewRequestIdRef.current++;
    setTdcCartId(null);
    setHoldSeatsError(null);
    setSeatChargeError(null);
    setPurchaseError(null);

    try {
      const holdResponse = await holdSeasonTickets({
        planPackageId: selectedPlanPackageId,
        section,
        seatIds,
        eventType,
        tdcPackageListId,
        tdcPackageBuyerTypeId,
        tdcPackageId,
      });
      setHoldExpiresAt(
        holdResponse.expiresAt ? new Date(holdResponse.expiresAt) : null,
      );
      setTdcCartId(holdResponse.tdcCart?.cartId || null);
      setConfirmedParkingSelections([]);
      setConfirmedParkingSection(undefined);
      setConfirmedParkingSectionLabel(undefined);
      if (tdcParkingPackageListId && tdcPackageId) {
        await fetchParkingSections(true);
      }
    } catch {
      setHoldSeatsError("Couldn't reserve seats. Please try again.");
      toast.error("Failed to reserve seats. Please try again.");
      return;
    }
    setCartSeats(seats);
    setSeatCharge(previewSeatCharge);
    setPreviewSeatCharge(null);
  };

  useEffect(() => {
    if (!pendingHoldSeats) return;
    if (!isLoggedIn) return;
    holdSelectedSeats(pendingHoldSeats)
      .catch(() => undefined)
      .finally(() => setPendingHoldSeats(null));
  }, [isLoggedIn, pendingHoldSeats]);

  if (!group) {
    return (
      <View className="bg-white rounded-xl p-6 gap-2">
        <Text category="h6">Select Your Plan</Text>
        <Text appearance="hint">Choose a plan group to continue.</Text>
      </View>
    );
  }

  const isSectionListLoading = normalizedSelectedSection
    ? isFetchingBlocks
    : isFetchingSections;

  const seatRowContext = useMemo(() => {
    const seatGroups = selectedPlanPackage?.primaryComponent?.seatGroups || [];
    const matchingSeatGroup =
      seatGroups.find(
        (sg) => sg.venueName === venueName && sg.eventType === eventType,
      ) || seatGroups[0];

    if (!matchingSeatGroup || !venueName || !eventType) return undefined;
    return {
      venueId: matchingSeatGroup.venueId,
      venueName,
      eventTypeName: eventType,
    };
  }, [eventType, selectedPlanPackage?.primaryComponent?.seatGroups, venueName]);

  const { data: seatsByRow, isFetching: isFetchingSeatsByRow } =
    useFetchSeatsBySectionQuery(
      seatRowContext?.venueId,
      normalizedSelectedSection,
      seatRowContext?.eventTypeName,
    );

  const handleSeatMapSectionAdded = (sections: string) => {
    setSelectedSection(sections);
    setSelectedSeats([]);
    setCartSeats([]);
    setHoldExpiresAt(null);
    setPendingConfirmPurchase(false);
    setPreviewSeatCharge(null);
    setSeatCharge(null);
    setTdcCartId(null);
    setHoldSeatsError(null);
    setSeatChargeError(null);
    setParkingSectionsError(null);
    setParkingSubmitError(null);
    setPurchaseError(null);
  };

  const handleSeatMapSectionRemoved = (_sections: string) => {
    setSelectedSection(undefined);
    setSelectedSeats([]);
    setCartSeats([]);
    setHoldExpiresAt(null);
    setPendingConfirmPurchase(false);
    setPreviewSeatCharge(null);
    setSeatCharge(null);
    setTdcCartId(null);
    setHoldSeatsError(null);
    setSeatChargeError(null);
    setParkingSectionsError(null);
    setParkingSubmitError(null);
    setPurchaseError(null);
  };

  const handleBackToSections = () => {
    setSelectedSection(undefined);
    setVenueMapKey((k) => k + 1);
    setSelectedSeats([]);
    setCartSeats([]);
    setHoldExpiresAt(null);
    setPendingConfirmPurchase(false);
    setPreviewSeatCharge(null);
    setSeatCharge(null);
    setTdcCartId(null);
    setHoldSeatsError(null);
    setSeatChargeError(null);
    setParkingSectionsError(null);
    setParkingSubmitError(null);
    setPurchaseError(null);
  };

  const selectedSeatIds = useMemo(() => {
    return selectedSeats
      .map((s) => s.id)
      .filter((id): id is number => typeof id === "number")
      .sort((a, b) => a - b);
  }, [selectedSeats]);

  const chargePreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const chargePreviewRequestIdRef = useRef(0);
  const autoSelectedSeatSectionRef = useRef<string | null>(null);

  const clearHoldCartState = () => {
    if (chargePreviewDebounceRef.current) {
      clearTimeout(chargePreviewDebounceRef.current);
    }
    chargePreviewRequestIdRef.current++;

    setSelectedSeats([]);
    setCartSeats([]);
    setHoldExpiresAt(null);
    setHoldRemainingMs(null);
    setPendingConfirmPurchase(false);
    setPreviewSeatCharge(null);
    setSeatCharge(null);
    setTdcCartId(null);
    setParkingSections([]);
    setSelectedParkingSection(undefined);
    setParkingDialogVisible(false);
    setSelectedParkingQuantity(1);
    setConfirmedParkingSelections([]);
    setConfirmedParkingSection(undefined);
    setConfirmedParkingSectionLabel(undefined);
    setHoldSeatsError(null);
    setSeatChargeError(null);
    setParkingSectionsError(null);
    setParkingSubmitError(null);
    setPurchaseError(null);
  };

  const handlePlanChange = async (plan: CmsPlan) => {
    if (selectedCmsPlan?._key === plan._key) {
      setActualPlanPackageInBuyFlowSlice(plan);
      return;
    }

    setLoginModalVisible(false);
    setVerifyPhonePopupVisible(false);
    setPendingHoldSeats(null);

    try {
      if (cartSeats.length > 0 || tdcCartId) {
        await deleteTicketCart();
      }
    } catch {
      toast.error("Failed to release tickets. Please try again.");
    } finally {
      clearHoldCartState();
    }

    setSelectedSection(undefined);
    setVenueMapKey((k) => k + 1);
    setActualPlanPackageInBuyFlowSlice(plan);
  };

  const handleReleaseTickets = async () => {
    try {
      await deleteTicketCart();
      clearHoldCartState();
    } catch {
      toast.error("Failed to release tickets. Please try again.");
    }
  };

  useEffect(() => {
    if (cartSeats.length > 0) return;
    if (pendingHoldSeats) return;
    if (isHoldingSeasonTickets) return;
    if (!selectedPlanPackageId || !normalizedSelectedSection || !eventType) {
      setPreviewSeatCharge(null);
      return;
    }
    if (selectedSeatIds.length === 0) {
      setPreviewSeatCharge(null);
      return;
    }

    if (chargePreviewDebounceRef.current) {
      clearTimeout(chargePreviewDebounceRef.current);
    }
    const requestId = ++chargePreviewRequestIdRef.current;
    chargePreviewDebounceRef.current = setTimeout(() => {
      const seatSelections: TdcSeatSelection[] =
        tdcPackageListId && tdcPackageId
          ? selectedSeats.map((s) => ({
              section: s.section,
              row: s.row,
              seat: s.seat,
            }))
          : [];
      const tdcPackageSelections = buildTdcPackageSelections(
        seatSelections,
        confirmedParkingSelections,
      );
      calculateSeatChargePreview({
        planPackageId: selectedPlanPackageId,
        section: normalizedSelectedSection,
        seatIds: selectedSeatIds,
        eventType,
        tdcPackageBuyerTypeId: tdcPackageBuyerTypeId || undefined,
        tdcPackageSelections:
          tdcPackageSelections.length > 0 ? tdcPackageSelections : undefined,
      })
        .then((charge) => {
          if (chargePreviewRequestIdRef.current !== requestId) return;
          setPreviewSeatCharge(charge);
        })
        .catch(() => {
          if (chargePreviewRequestIdRef.current !== requestId) return;
          setPreviewSeatCharge(null);
        });
    }, 250);

    return () => {
      if (chargePreviewDebounceRef.current) {
        clearTimeout(chargePreviewDebounceRef.current);
      }
    };
  }, [
    calculateSeatChargePreview,
    cartSeats.length,
    confirmedParkingSelections,
    eventType,
    isHoldingSeasonTickets,
    normalizedSelectedSection,
    pendingHoldSeats,
    selectedPlanPackageId,
    selectedSeatIds,
    selectedSeats,
    tdcPackageId,
    tdcPackageListId,
  ]);

  useEffect(() => {
    autoSelectedSeatSectionRef.current = null;
  }, [
    normalizedSelectedSection,
    seatRowContext?.eventTypeName,
    seatRowContext?.venueId,
  ]);

  useEffect(() => {
    if (cartSeats.length > 0) return;
    if (!normalizedSelectedSection) return;
    if (autoSelectedSeatSectionRef.current === normalizedSelectedSection)
      return;
    if (isFetchingSeatsByRow || isSectionListLoading) return;
    if (!seatsByRow || Object.keys(seatsByRow).length === 0) return;
    if (selectedSeats.length > 0) return;
    if (availableSeatIds.size === 0) return;

    const rowEntries = Object.entries(seatsByRow);
    rowEntries.sort(([rowA], [rowB]) =>
      rowA.localeCompare(rowB, undefined, { numeric: true }),
    );

    for (const [, rowSeats] of rowEntries) {
      const sortedSeats = [...rowSeats].sort((a, b) => {
        const seatA = a.seatDisplay || a.seat;
        const seatB = b.seatDisplay || b.seat;
        return seatA.localeCompare(seatB, undefined, { numeric: true });
      });

      const firstAvailable = sortedSeats.find(
        (s) => typeof s.id === "number" && availableSeatIds.has(s.id),
      );
      if (firstAvailable) {
        autoSelectedSeatSectionRef.current = normalizedSelectedSection;
        setSelectedSeats([firstAvailable]);
        break;
      }
    }
  }, [
    availableSeatIds,
    cartSeats.length,
    isFetchingSeatsByRow,
    isSectionListLoading,
    normalizedSelectedSection,
    seatsByRow,
    selectedSeats.length,
  ]);

  const summarySeats = cartSeats.length > 0 ? cartSeats : selectedSeats;
  const summaryCharge = cartSeats.length > 0 ? seatCharge : previewSeatCharge;
  const isSummaryLoading =
    cartSeats.length > 0 ? isCalculatingSeatCharge : isPreviewingSeatCharge;
  const isCartActive = cartSeats.length > 0;

  const hasPaymentMethodOnFile = !!account?.billingLast4;
  const isParkingRequired = !!tdcParkingPackageListId;
  const hasRequiredParking =
    !isParkingRequired || confirmedParkingSelections.length > 0;
  const shouldShowPaymentSection =
    cartSeats.length > 0 && isLoggedIn && hasRequiredParking;
  const shouldShowParkingBanner =
    cartSeats.length > 0 && (isParkingRequired || !!parkingSectionsError);
  const chargeTotal = useMemo(() => {
    if (!seatCharge) return 0;
    // ?? not ||: a totalCreditCardCharges of "0" is a real card charge of $0
    // (e.g. fully covered by account credit) and must not fall back to the
    // order's full price.
    const raw = seatCharge.totalCreditCardCharges ?? seatCharge.totalPrice;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [seatCharge]);

  const handlePurchaseWithSavedPaymentMethod = async () => {
    if (!selectedPlanPackageId || cartSeats.length === 0) {
      toast.error("Missing seat selection. Please reserve seats again.");
      return;
    }
    if (!hasRequiredParking) {
      toast.error("Please add the required parking selection first.");
      return;
    }
    if (!selectedBillingInterval) {
      toast.error("Missing billing interval.");
      return;
    }

    const seatIds = cartSeats
      .map((s) => s.id)
      .filter((id): id is number => typeof id === "number");
    if (seatIds.length === 0) {
      toast.error("Missing seat selection. Please reserve seats again.");
      return;
    }
    if (isTdcSeasonTicketPlan && !tdcCartId) {
      toast.error("Missing ticket cart. Please reserve seats again.");
      return;
    }

    const needsPhoneVerification =
      !!currentUser &&
      !currentUser.phoneGrandfathered &&
      !currentUser.phoneVerified &&
      tenantConfig?.requirePhoneVerification !== false;
    if (needsPhoneVerification) {
      setPendingConfirmPurchase(true);
      setVerifyPhonePopupVisible(true);
      return;
    }

    try {
      setPurchaseError(null);
      await confirmSeasonTicketPurchase({
        planPackageId: selectedPlanPackageId,
        seatIds,
        billingInterval: selectedBillingInterval,
        eventType,
        tdcCartId: tdcCartId || undefined,
        tdcPackageListId,
        tdcPackageId,
        tdcPackageBuyerTypeId: tdcPackageBuyerTypeId || undefined,
      });
      toast.success("Purchase complete.");
      toDashboard();
    } catch {
      setPurchaseError("Purchase failed. Please try again.");
      toast.error("Failed to complete purchase. Please try again.");
    }
  };

  const handlePaymentTokenReceived = async (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse,
  ) => {
    if (error) {
      toast.error(error.error.message);
      return;
    }
    if (!response) return;

    if (!account?.id) {
      toast.success("Payment method captured.");
      return;
    }

    const confirmPurchaseAndRedirect = async () => {
      if (!selectedPlanPackageId || cartSeats.length === 0) {
        toast.error("Missing seat selection. Please reserve seats again.");
        return;
      }
      if (!hasRequiredParking) {
        toast.error("Please add the required parking selection first.");
        return;
      }
      if (!selectedBillingInterval) {
        toast.error("Missing billing interval.");
        return;
      }

      const seatIds = cartSeats
        .map((s) => s.id)
        .filter((id): id is number => typeof id === "number");
      if (seatIds.length === 0) {
        toast.error("Missing seat selection. Please reserve seats again.");
        return;
      }
      if (isTdcSeasonTicketPlan && !tdcCartId) {
        toast.error("Missing ticket cart. Please reserve seats again.");
        return;
      }
      setPurchaseError(null);
      await confirmSeasonTicketPurchase({
        planPackageId: selectedPlanPackageId,
        seatIds,
        billingInterval: selectedBillingInterval,
        eventType,
        tdcCartId: tdcCartId || undefined,
        tdcPackageListId,
        tdcPackageId,
        tdcPackageBuyerTypeId: tdcPackageBuyerTypeId || undefined,
      });
      toast.success("Purchase complete.");
      toDashboard();
    };

    try {
      await updateBilling({ accountId: account.id, token: response });
      await fetchAccount();
    } catch {
      toast.error("Failed to save payment method.");
      return;
    }

    const needsPhoneVerification =
      !!currentUser &&
      !currentUser.phoneGrandfathered &&
      !currentUser.phoneVerified &&
      tenantConfig?.requirePhoneVerification !== false;
    if (needsPhoneVerification) {
      setPendingConfirmPurchase(true);
      setVerifyPhonePopupVisible(true);
      return;
    }

    try {
      await confirmPurchaseAndRedirect();
    } catch {
      setPurchaseError("Purchase failed. Please try again.");
      toast.error("Failed to complete purchase. Please try again.");
    }
  };

  useEffect(() => {
    if (!pendingConfirmPurchase) return;
    if (!currentUser) return;
    if (currentUser.phoneGrandfathered || currentUser.phoneVerified) {
      setPendingConfirmPurchase(false);

      if (!selectedPlanPackageId || cartSeats.length === 0) {
        toast.error("Missing seat selection. Please reserve seats again.");
        return;
      }
      if (!hasRequiredParking) {
        toast.error("Please add the required parking selection first.");
        return;
      }
      if (!selectedBillingInterval) {
        toast.error("Missing billing interval.");
        return;
      }

      const seatIds = cartSeats
        .map((s) => s.id)
        .filter((id): id is number => typeof id === "number");
      if (seatIds.length === 0) {
        toast.error("Missing seat selection. Please reserve seats again.");
        return;
      }
      if (isTdcSeasonTicketPlan && !tdcCartId) {
        toast.error("Missing ticket cart. Please reserve seats again.");
        return;
      }

      confirmSeasonTicketPurchase({
        planPackageId: selectedPlanPackageId,
        seatIds,
        billingInterval: selectedBillingInterval,
        eventType,
        tdcCartId: isTdcSeasonTicketPlan && tdcCartId ? tdcCartId : undefined,
        tdcPackageListId,
        tdcPackageId,
        tdcPackageBuyerTypeId: tdcPackageBuyerTypeId || undefined,
      })
        .then(() => {
          setPurchaseError(null);
          toast.success("Purchase complete.");
          toDashboard();
        })
        .catch(() => {
          setPurchaseError("Purchase failed. Please try again.");
          toast.error("Failed to complete purchase. Please try again.");
        });
    }
  }, [
    cartSeats,
    confirmSeasonTicketPurchase,
    currentUser,
    eventType,
    hasRequiredParking,
    isTdcSeasonTicketPlan,
    pendingConfirmPurchase,
    selectedBillingInterval,
    selectedPlanPackageId,
    tdcCartId,
  ]);

  useEffect(() => {
    if (maxParkingQuantity === 0) return;
    if (selectedParkingQuantity <= maxParkingQuantity) return;
    setSelectedParkingQuantity(maxParkingQuantity);
  }, [maxParkingQuantity, selectedParkingQuantity]);

  const handleReserveSelectedSeats = async () => {
    if (!normalizedSelectedSection) {
      toast.error("Select a section first.");
      return;
    }
    if (selectedSeats.length === 0) return;

    if (!isLoggedIn) {
      setPendingHoldSeats(selectedSeats);
      setLoginModalVisible(true);
      return;
    }

    await holdSelectedSeats(selectedSeats);
  };

  const handleSubmitParkingSelection = async () => {
    if (!selectedParkingSection) {
      toast.error("Select a parking section first.");
      return;
    }
    if (!tdcParkingPackageListId || !tdcParkingPackageBuyerTypeId) {
      toast.error("Missing parking package configuration.");
      return;
    }
    if (selectedParkingQuantity <= 0) return;
    setParkingSubmitError(null);

    const ticketIds = parkingTicketIds.slice(0, selectedParkingQuantity);
    if (ticketIds.length < selectedParkingQuantity) {
      toast.error("Not enough parking spaces available.");
      return;
    }
    const parkingSelections = buildParkingSelections(selectedParkingQuantity);
    if (parkingSelections.length < selectedParkingQuantity) {
      toast.error("Not enough parking spaces available.");
      return;
    }

    try {
      const cart = await addParkingPackageSeats({
        tdcPackageListId: tdcParkingPackageListId,
        tdcBuyerTypeId: tdcParkingPackageBuyerTypeId,
        ticketIds,
        tdcPackageId,
      });
      if (cart?.expiresAt) {
        setHoldExpiresAt(new Date(cart.expiresAt));
      }
      if (cart?.cartId) {
        setTdcCartId(cart.cartId);
      }
      setConfirmedParkingSelections(parkingSelections);
      setConfirmedParkingSection(selectedParkingSection);
      const sectionLabel =
        parkingSections.find((s) => s.section === selectedParkingSection)
          ?.sectionDisplay || selectedParkingSection;
      setConfirmedParkingSectionLabel(sectionLabel);
      setParkingDialogVisible(false);

      if (selectedPlanPackageId && cartSeats.length > 0) {
        const seatSelections: TdcSeatSelection[] =
          tdcPackageListId && tdcPackageId
            ? cartSeats.map((s) => ({
                section: s.section,
                row: s.row,
                seat: s.seat,
              }))
            : [];
        const tdcPackageSelections = buildTdcPackageSelections(
          seatSelections,
          parkingSelections,
        );
        try {
          const seatIds = cartSeats
            .map((s) => s.id)
            .filter((id): id is number => typeof id === "number");
          const charge = await calculateSeatChargeFinal({
            planPackageId: selectedPlanPackageId,
            section: normalizedSelectedSection || cartSeats[0]?.section,
            seatIds,
            eventType,
            tdcPackageBuyerTypeId: tdcPackageBuyerTypeId || undefined,
            tdcPackageSelections:
              tdcPackageSelections.length > 0
                ? tdcPackageSelections
                : undefined,
          });
          setSeatCharge(charge);
          setSeatChargeError(null);
        } catch {
          setSeatChargeError("Parking added, but failed to refresh charges.");
          toast.error("Parking added, but failed to refresh charges.");
        }
      }
    } catch {
      setParkingSubmitError("Couldn't add parking spaces. Please try again.");
      toast.error("Failed to add parking spaces. Please try again.");
    }
  };

  const handleRetrySeatCharge = async () => {
    if (!selectedPlanPackageId || cartSeats.length === 0) return;
    const seatIds = cartSeats
      .map((s) => s.id)
      .filter((id): id is number => typeof id === "number");
    if (seatIds.length === 0) return;

    const seatSelections: TdcSeatSelection[] =
      tdcPackageListId && tdcPackageId
        ? cartSeats.map((s) => ({
            section: s.section,
            row: s.row,
            seat: s.seat,
          }))
        : [];
    const tdcPackageSelections = buildTdcPackageSelections(
      seatSelections,
      confirmedParkingSelections,
    );

    try {
      setSeatChargeError(null);
      const charge = await calculateSeatChargeFinal({
        planPackageId: selectedPlanPackageId,
        section: normalizedSelectedSection || cartSeats[0]?.section,
        seatIds,
        eventType,
        tdcPackageBuyerTypeId: tdcPackageBuyerTypeId || undefined,
        tdcPackageSelections:
          tdcPackageSelections.length > 0 ? tdcPackageSelections : undefined,
      });
      setSeatCharge(charge);
    } catch {
      setSeatChargeError("Failed to calculate charges. Please try again.");
      toast.error("Failed to calculate charges. Please try again.");
    }
  };

  const handleRetryHoldSeats = async () => {
    if (selectedSeats.length === 0) return;
    await holdSelectedSeats(selectedSeats);
  };

  const handleOpenParkingDialog = async () => {
    await fetchParkingSections(true);
  };

  useEffect(() => {
    if (!holdExpiresAt) {
      setHoldRemainingMs(null);
      return;
    }
    const tick = () => setHoldRemainingMs(holdExpiresAt.getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [holdExpiresAt]);

  const holdCountdownText = useMemo(() => {
    if (!holdExpiresAt || holdRemainingMs === null) return null;
    const totalSeconds = Math.max(0, Math.floor(holdRemainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (totalSeconds <= 0) return "Hold expired. Please reserve again.";
    return `Seats held — expires in ${minutes}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  }, [holdExpiresAt, holdRemainingMs]);

  return (
    <View className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
      {/* Plans (left) */}
      <View>
        <Text category="h6">Select Your Plan</Text>
        <View className="flex-col justify-center">
          {shouldShowBillingInterval && (
            <View
              className="mt-4 flex-row justify-center items-center border rounded-lg overflow-hidden text-center text-white"
              style={{ backgroundColor: branding?.primaryColor }}
            >
              {billingIntervals.map((interval) => {
                const selected = interval.value === selectedBillingInterval;
                return (
                  <Pressable
                    key={interval.value}
                    className={classNames(
                      "p-1 border rounded-md border-transparent grow text-white",
                    )}
                    onPress={() =>
                      dispatch(
                        setSelectedBillingInterval(
                          interval.value as BillingInterval,
                        ),
                      )
                    }
                  >
                    <div
                      className={classNames("rounded-md p-2", {
                        "text-black": selected,
                        "bg-white": selected,
                      })}
                    >
                      {interval.label}
                    </div>
                  </Pressable>
                );
              })}
            </View>
          )}

          <View className="mt-4 grid gap-4 relative">
            <View>
              {Cmsplans.map((plan) => {
                const isPlanSelected = selectedCmsPlan?._key === plan._key;

                return (
                  <View key={plan._key} className="mb-4">
                    <View
                      className={classNames("p-2 rounded-xl mb-2", {
                        "border-[4px]": isPlanSelected,
                      })}
                      style={{
                        borderColor: branding?.primaryColor,
                      }}
                    >
                      <PlanContainer
                        plan={plan}
                        onPlanPress={handlePlanChange}
                        isPlanSelected={isPlanSelected}
                        hidePrice={true}
                      ></PlanContainer>
                    </View>

                    {/* Mobile: show seat map under each plan */}
                    {!isDesktop && canRenderSeatMap && isPlanSelected && (
                      <View className="md:hidden rounded-xl overflow-hidden bg-white">
                        <View style={{ height: 260 }}>
                          <VenueSeatMap
                            mapId={`season-ticket-seat-map-${plan._key}`}
                            tenantType={branding!.type}
                            tenantSubdomain={tenantSubdomain!}
                            theme={tenantThemeColor}
                            eventType={eventType!}
                            venueName={venueName!}
                            selectedPartySize={1}
                            blocks={seatMapSections as any}
                            seatGroups={
                              selectedPlanPackage?.primaryComponent
                                ?.seatGroups || []
                            }
                            multipleSectionsAllowed={false}
                            includePremium={true}
                            onSectionAdded={handleSeatMapSectionAdded}
                            onSectionRemoved={handleSeatMapSectionRemoved}
                            onReset={handleBackToSections}
                            preferredSections={
                              normalizedSelectedSection
                                ? [normalizedSelectedSection]
                                : []
                            }
                            venueMapKey={venueMapKey}
                          />
                        </View>
                        <View className="p-2">
                          {isSeatMapSectionsError && (
                            <InlineErrorBanner
                              message="Couldn't load available sections."
                              actionLabel="Retry"
                              onAction={() => refetchSeatMapSections()}
                            />
                          )}
                          {!!normalizedSelectedSection ? (
                            <>
                              {isSeatBlocksError && (
                                <InlineErrorBanner
                                  message="Couldn't load seats for this section."
                                  actionLabel="Retry"
                                  onAction={() => refetchSeatBlocks()}
                                />
                              )}
                              <AvailableSeatDots
                                seatsByRow={seatsByRow}
                                availableSeatIds={availableSeatIds}
                                selectedSeats={selectedSeats}
                                primaryColor={branding?.primaryColor}
                                loading={
                                  isSectionListLoading || isFetchingSeatsByRow
                                }
                                disabled={isCartActive}
                                onSelectionChange={setSelectedSeats}
                              />
                              <View className="mt-3">
                                <Button
                                  onPress={handleReserveSelectedSeats}
                                  disabled={
                                    isCartActive ||
                                    selectedSeats.length === 0 ||
                                    isSectionListLoading ||
                                    isHoldingSeasonTickets ||
                                    isCalculatingSeatCharge
                                  }
                                  loading={isHoldingSeasonTickets}
                                >
                                  Reserve
                                </Button>
                              </View>
                              {holdSeatsError && (
                                <InlineErrorBanner
                                  message={holdSeatsError}
                                  actionLabel="Retry"
                                  onAction={handleRetryHoldSeats}
                                />
                              )}
                            </>
                          ) : (
                            <Text appearance="hint" category="c1">
                              Select a section on the map to see seats.
                            </Text>
                          )}

                          {summarySeats.length > 0 && (
                            <View className="mt-3 pt-3 border-t">
                              {cartSeats.length > 0 && holdCountdownText && (
                                <View className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                                  <View className="flex-row items-center justify-between gap-3">
                                    <Text category="c1">
                                      {holdCountdownText}
                                    </Text>
                                    <Button
                                      appearance="ghost"
                                      size="tiny"
                                      onPress={handleReleaseTickets}
                                      disabled={
                                        isDeletingTicketCart ||
                                        isHoldingSeasonTickets ||
                                        isConfirmingSeasonTicketPurchase
                                      }
                                      loading={isDeletingTicketCart}
                                    >
                                      Clear Cart
                                    </Button>
                                  </View>
                                </View>
                              )}
                              {shouldShowParkingBanner &&
                                !hasRequiredParking && (
                                  <InlineErrorBanner
                                    message={
                                      parkingSectionsError ||
                                      "Parking selection required to continue."
                                    }
                                    actionLabel="Select Parking"
                                    onAction={handleOpenParkingDialog}
                                  />
                                )}
                              {seatChargeError && (
                                <InlineErrorBanner
                                  message={seatChargeError}
                                  actionLabel="Retry"
                                  onAction={handleRetrySeatCharge}
                                />
                              )}
                              <SeasonPlanOrderSummary
                                seats={summarySeats}
                                charge={summaryCharge}
                                isLoading={isSummaryLoading}
                                parkingSpacesCount={
                                  confirmedParkingSelections.length > 0
                                    ? confirmedParkingSelections.length
                                    : undefined
                                }
                                parkingSectionLabel={
                                  confirmedParkingSectionLabel
                                }
                                parkingSelections={confirmedParkingSelections}
                              />
                            </View>
                          )}

                          {shouldShowPaymentSection && (
                            <View className="mt-4 pt-4 border-t">
                              <View className="rounded-xl my-4 shadow-lg font-semibold text-lg">
                                <View className="p-4">
                                  {purchaseError && (
                                    <InlineErrorBanner
                                      message={purchaseError}
                                    />
                                  )}
                                  <Spacer>
                                    <Text category="h6">Payment</Text>
                                  </Spacer>

                                  {hasPaymentMethodOnFile ? (
                                    <Button
                                      onPress={
                                        handlePurchaseWithSavedPaymentMethod
                                      }
                                      disabled={
                                        !seatCharge ||
                                        isCalculatingSeatCharge ||
                                        isConfirmingSeasonTicketPurchase
                                      }
                                      loading={isConfirmingSeasonTicketPurchase}
                                    >
                                      {`Purchase ${formatCurrency(
                                        chargeTotal,
                                      )}`}
                                    </Button>
                                  ) : (
                                    <StripePaymentForm
                                      btnText={`Pay ${formatCurrency(
                                        chargeTotal,
                                      )}`}
                                      walletAmount={seatCharge ? chargeTotal : null}
                                      onTokenReceived={
                                        handlePaymentTokenReceived
                                      }
                                      disabled={
                                        !seatCharge ||
                                        isCalculatingSeatCharge ||
                                        isConfirmingSeasonTicketPurchase
                                      }
                                      loading={
                                        isUpdatingBilling ||
                                        isConfirmingSeasonTicketPurchase
                                      }
                                    />
                                  )}
                                </View>
                              </View>
                            </View>
                          )}
                        </View>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          </View>

          {isDesktop && summarySeats.length > 0 && (
            <View className="mt-4 pt-4 border-t">
              {cartSeats.length > 0 && holdCountdownText && (
                <View className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <View className="flex-row items-center justify-between gap-3">
                    <Text category="c1">{holdCountdownText}</Text>
                    <Button
                      appearance="ghost"
                      size="tiny"
                      onPress={handleReleaseTickets}
                      disabled={
                        isDeletingTicketCart ||
                        isHoldingSeasonTickets ||
                        isConfirmingSeasonTicketPurchase
                      }
                      loading={isDeletingTicketCart}
                    >
                      Clear Cart
                    </Button>
                  </View>
                </View>
              )}
              {shouldShowParkingBanner && !hasRequiredParking && (
                <InlineErrorBanner
                  message={
                    parkingSectionsError ||
                    "Parking selection required to continue."
                  }
                  actionLabel="Select Parking"
                  onAction={handleOpenParkingDialog}
                />
              )}
              {seatChargeError && (
                <InlineErrorBanner
                  message={seatChargeError}
                  actionLabel="Retry"
                  onAction={handleRetrySeatCharge}
                />
              )}
              <SeasonPlanOrderSummary
                seats={summarySeats}
                charge={summaryCharge}
                isLoading={isSummaryLoading}
                parkingSpacesCount={
                  confirmedParkingSelections.length > 0
                    ? confirmedParkingSelections.length
                    : undefined
                }
                parkingSectionLabel={confirmedParkingSectionLabel}
                parkingSelections={confirmedParkingSelections}
              />

              {shouldShowPaymentSection && (
                <View className="mt-4 pt-4 border-t">
                  <Text category="h6">Payment</Text>
                  <View className="p-4">
                    {purchaseError && (
                      <InlineErrorBanner message={purchaseError} />
                    )}
                    {hasPaymentMethodOnFile ? (
                      <Button
                        onPress={handlePurchaseWithSavedPaymentMethod}
                        disabled={
                          !seatCharge ||
                          isCalculatingSeatCharge ||
                          isConfirmingSeasonTicketPurchase
                        }
                        loading={isConfirmingSeasonTicketPurchase}
                      >
                        {`Purchase ${formatCurrency(chargeTotal)}`}
                      </Button>
                    ) : (
                      <View className="bg-white shadow-lg rounded-xl my-4 font-semibold text-lg p-4">
                        <StripePaymentForm
                          btnText={`Pay ${formatCurrency(chargeTotal)}`}
                          walletAmount={seatCharge ? chargeTotal : null}
                          onTokenReceived={handlePaymentTokenReceived}
                          disabled={
                            !seatCharge ||
                            isCalculatingSeatCharge ||
                            isConfirmingSeasonTicketPurchase
                          }
                          loading={
                            isUpdatingBilling ||
                            isConfirmingSeasonTicketPurchase
                          }
                        />
                      </View>
                    )}
                  </View>
                </View>
              )}
            </View>
          )}
        </View>
      </View>

      {/* Seat map (right, desktop sticky) */}
      <View className="hidden md:block sticky top-0">
        {isDesktop && canRenderSeatMap && (
          <View className="bg-white rounded-xl overflow-hidden">
            <View style={{ height: 400 }}>
              <VenueSeatMap
                mapId="season-ticket-seat-map"
                tenantType={branding!.type}
                tenantSubdomain={tenantSubdomain!}
                theme={tenantThemeColor}
                eventType={eventType!}
                venueName={venueName!}
                selectedPartySize={1}
                blocks={seatMapSections as any}
                seatGroups={
                  selectedPlanPackage?.primaryComponent?.seatGroups || []
                }
                multipleSectionsAllowed={false}
                includePremium={true}
                onSectionAdded={handleSeatMapSectionAdded}
                onSectionRemoved={handleSeatMapSectionRemoved}
                onReset={handleBackToSections}
                preferredSections={
                  normalizedSelectedSection ? [normalizedSelectedSection] : []
                }
                venueMapKey={venueMapKey}
              />
            </View>
            <View className="p-2 border-t">
              {isSeatMapSectionsError && (
                <InlineErrorBanner
                  message="Couldn't load available sections."
                  actionLabel="Retry"
                  onAction={() => refetchSeatMapSections()}
                />
              )}
              {!!normalizedSelectedSection ? (
                <>
                  {isSeatBlocksError && (
                    <InlineErrorBanner
                      message="Couldn't load seats for this section."
                      actionLabel="Retry"
                      onAction={() => refetchSeatBlocks()}
                    />
                  )}
                  <AvailableSeatDots
                    seatsByRow={seatsByRow}
                    availableSeatIds={availableSeatIds}
                    selectedSeats={selectedSeats}
                    primaryColor={branding?.primaryColor}
                    loading={isSectionListLoading || isFetchingSeatsByRow}
                    disabled={isCartActive}
                    onSelectionChange={setSelectedSeats}
                  />
                  <View className="mt-3">
                    <Button
                      onPress={handleReserveSelectedSeats}
                      disabled={
                        isCartActive ||
                        selectedSeats.length === 0 ||
                        isSectionListLoading ||
                        isHoldingSeasonTickets ||
                        isCalculatingSeatCharge
                      }
                      loading={isHoldingSeasonTickets}
                    >
                      Reserve
                    </Button>
                  </View>
                  {holdSeatsError && (
                    <InlineErrorBanner
                      message={holdSeatsError}
                      actionLabel="Retry"
                      onAction={handleRetryHoldSeats}
                    />
                  )}
                </>
              ) : (
                <Text appearance="hint" category="c1">
                  Select a section on the map to see seats.
                </Text>
              )}
            </View>
          </View>
        )}
      </View>

      <Dialog
        visible={loginModalVisible}
        cancelText="Close"
        onCancel={() => {
          setLoginModalVisible(false);
          setPendingHoldSeats(null);
        }}
      >
        <ScrollView>
          <LoginOrSignupForm
            initialForm="signup"
            marketplace
            loginTitle="Log in to reserve seats"
            signupTitle="Create an account to reserve seats"
          />
        </ScrollView>
      </Dialog>

      <Dialog
        visible={parkingDialogVisible}
        setVisible={setParkingDialogVisible}
        cancelText="Close"
      >
        <ScrollView>
          <Text category="h6">Select Parking Section</Text>
          <Spacer />
          {parkingSectionsError && (
            <InlineErrorBanner
              message={parkingSectionsError}
              actionLabel="Retry"
              onAction={handleOpenParkingDialog}
            />
          )}
          {parkingSubmitError && (
            <InlineErrorBanner message={parkingSubmitError} />
          )}
          {isFetchingParkingSections ? (
            <Text appearance="hint">Loading parking sections...</Text>
          ) : parkingSections.length === 0 ? (
            <Text appearance="hint">No parking sections available.</Text>
          ) : (
            <View className="gap-2">
              {parkingSections.map((section) => {
                const sectionValue = section.section;
                const isSelected = sectionValue === selectedParkingSection;
                return (
                  <Pressable
                    key={sectionValue}
                    onPress={() => handleSelectParkingSection(sectionValue)}
                  >
                    <View
                      className={classNames("border rounded-lg px-3 py-2", {
                        "border-2": isSelected,
                        "border-gray-200": !isSelected,
                      })}
                      style={{
                        borderColor: isSelected
                          ? branding?.primaryColor
                          : undefined,
                      }}
                    >
                      <Text>{section.sectionDisplay || section.section}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {selectedParkingSection && (
            <>
              <Spacer />
              <Text category="c1">Select Quantity</Text>
              <Spacer />
              {isParkingBlocksError ? (
                <InlineErrorBanner
                  message="Couldn't load available spaces."
                  actionLabel="Retry"
                  onAction={() => refetchParkingBlocks()}
                />
              ) : isFetchingParkingBlocks ? (
                <Text appearance="hint">Loading available spaces...</Text>
              ) : maxParkingQuantity === 0 ? (
                <Text appearance="hint">
                  No parking spaces available for this section.
                </Text>
              ) : (
                <>
                  <SeatQuantitySelector
                    size={maxParkingQuantity}
                    selectedPartySize={selectedParkingQuantity}
                    onChange={setSelectedParkingQuantity}
                    eventType="PARKING"
                  />
                  <Spacer />
                  <Button
                    onPress={handleSubmitParkingSelection}
                    disabled={isAddingParkingPackageSeats}
                    loading={isAddingParkingPackageSeats}
                  >
                    Submit
                  </Button>
                </>
              )}
            </>
          )}
        </ScrollView>
      </Dialog>

      <VerifyPhonePopup
        visible={verifyPhonePopupVisible}
        onCancel={() => {
          setVerifyPhonePopupVisible(false);
          setPendingConfirmPurchase(false);
        }}
      />
    </View>
  );
};

export default SeasonTicketCheckoutFlow;
